import React from 'react';
import {
    Box,
    Button,
    CarouselSlide,
    Column,
    FlexBox,
    FluidImage,
    GridContainer,
    H1,
    Icon,
    Link,
    Row,
    SelectionSet,
    ThumbnailsHero,
    ThumbnailsHeroImagesSlidesContainer,
    ThumbnailsHeroThumbnailsSlidesContainer,
    Typography,
    tokens,
} from '@vp/swan';
import { scrollToElementById } from '@99designs/browser';
import { useInVistaExperimentVariant } from '@99designs/experiments';
import { sendNavigationEvent, usePage } from '@99designs/tracking';
import { buildSrcset } from '../../../..';
import { TierSelectionInput } from '../TierSelectionInput';
import { BASE_TIER } from '../pageData/tierConfig';
import { ComparePackagesSectionId } from '../pageData/tierServiceDetailPage';
import clickEventData from '../utils/clickEventData';
import type { Images } from '../TierLayout';
import type { Tier } from '../pageData/tierConfig';

export interface HeaderProps {
    title: string;
    tiers: Tier[];
    locale: string;
    heroImages: Images;
}

export function TierHeroBanner({ title, tiers, locale, heroImages }: HeaderProps) {
    const inVariant = useInVistaExperimentVariant('design_services_sdp_turnaround_time');
    const { pageData } = usePage();
    const [currentTier, setCurrentTier] = React.useState(BASE_TIER);
    const currentTierConfig = tiers.find((tier) => tier.id === currentTier) || tiers[0];

    const accessibleTextForDots = Array.from(
        Array(tiers.length),
        (_, index) => 'Show product preview ' + (index + 1)
    );

    const logoDesignURL = `/experts/services/details/logoessential?locale=${locale}`;
    const pageZone = 'Merchandising Section';

    return (
        <GridContainer>
            <Row>
                <Column span={6}>
                    <ThumbnailsHero accessibleTextForDots={accessibleTextForDots}>
                        <ThumbnailsHeroImagesSlidesContainer>
                            {heroImages?.map((image, i) => (
                                <CarouselSlide
                                    key={`carouselimage1-${i}`}
                                    backgroundColor={'standard'}
                                >
                                    <FluidImage
                                        srcSet={buildSrcset(image.cloudinaryImage)}
                                        alt={image.altTextOverride || ''}
                                        role="presentation"
                                        style={{
                                            height: 'auto',
                                            width: '100%',
                                            borderRadius: tokens.SwanBaseBorderRadius400,
                                        }}
                                    />
                                </CarouselSlide>
                            ))}
                        </ThumbnailsHeroImagesSlidesContainer>
                        {heroImages?.length >= 2 && (
                            <ThumbnailsHeroThumbnailsSlidesContainer>
                                {heroImages?.map((image, i) => (
                                    <CarouselSlide key={`carouselimage2-${i}`}>
                                        <FluidImage
                                            src={image.cloudinaryImage.thumbnailCloudinaryUrl || ''}
                                            alt=""
                                            role="presentation"
                                        />
                                    </CarouselSlide>
                                ))}
                            </ThumbnailsHeroThumbnailsSlidesContainer>
                        )}
                    </ThumbnailsHero>
                </Column>
                <Column span={6} sticky>
                    <FlexBox flexDirection="column">
                        <Typography fontSkin="body-standard-bold">{'Design Services'}</Typography>
                        <Box mt={2}>
                            <H1 mb={0} fontSkin="title-headline">
                                {title}
                            </H1>
                            <Typography fontSkin="body-standard" mt={2}>
                                {inVariant('variant_3_day')
                                    ? 'Professional designers on-hand to deliver custom design.'
                                    : 'Professional designers on-hand to deliver custom design, fast.'}
                            </Typography>
                        </Box>
                        <Box mt={7}>
                            <Typography fontSkin="body-standard-bold">
                                {'What type of design service are you looking for?'}
                            </Typography>
                        </Box>
                        <SelectionSet
                            variant="single-select"
                            selectedValue={currentTier}
                            onSelectedValueChange={(value) => setCurrentTier(value ?? currentTier)}
                        >
                            <GridContainer px={2}>
                                <Row marginTop={4}>
                                    {tiers.map((tier) => (
                                        <TierSelectionInput key={tier.id} {...tier} />
                                    ))}
                                </Row>
                            </GridContainer>
                        </SelectionSet>
                    </FlexBox>

                    <Box>
                        <FlexBox display="inline-block">
                            <Typography fontSize="small">
                                <b>Note:</b> Printing and
                                <Link
                                    to={logoDesignURL}
                                    mx={1}
                                    onClick={() => {
                                        sendNavigationEvent({
                                            ...clickEventData({
                                                pagePath: pageData.pagePath,
                                                ctaUrl: logoDesignURL,
                                                ctaValue: 'Logo design-CTA',
                                                pageZone,
                                            }),
                                            ...pageData,
                                        });
                                    }}
                                >
                                    logo design
                                </Link>
                                costs are additional. Turnaround time excludes weekends and public
                                holidays.
                            </Typography>
                        </FlexBox>

                        <Button
                            mt={6}
                            skin="primary"
                            width="full-width"
                            render={({ children, ...rest }) => (
                                <Link
                                    to={currentTierConfig.briefURL}
                                    onClick={() => {
                                        sendNavigationEvent({
                                            ...clickEventData({
                                                pagePath: pageData.pagePath,
                                                ctaUrl: currentTierConfig.briefURL,
                                                ctaValue: 'Get Started-CTA',
                                                pageZone,
                                            }),
                                            ...pageData,
                                        });
                                    }}
                                    {...rest}
                                >
                                    {children}
                                </Link>
                            )}
                        >
                            Continue to brief
                        </Button>

                        <Button
                            mt={4}
                            skin="secondary"
                            width="full-width"
                            onClick={() => scrollToElementById(ComparePackagesSectionId)}
                        >
                            Compare packages
                        </Button>
                    </Box>

                    <FlexBox justifyContent="center" mt={4}>
                        <Icon iconType="guaranteedSatisfaction" size="small" skin="subtle" mr={2} />
                        <Typography fontSize="small" fontSkin="body-small" textColor="subtle">
                            {'VistaPrint is here to help every step of the way'}
                        </Typography>
                    </FlexBox>
                </Column>
            </Row>
        </GridContainer>
    );
}
